/**
 * xr_ui_data — pure shaping of the rows the VR panel reads (XR P2 "VR UI / UX", 2026-09-28): the wrist's combat
 * strip (health, the weapon in hand, its magazine or capacitor — the SAME arithmetic the desktop combat HUD runs, from
 * lib/combat_core) and the Inventory page's list. No DB, no DOM: the module hands plain values in.
 */

import { chargeAfter } from './combat_core.js';
import type { WeaponSpec } from './weapon_catalog.js';

export interface FeedRow { itemKey: string; rounds: number; charge: number; chargeAtMs: number; reloadUntilMs: number }
export interface AmmoReadout { text: string; frac: number; fed: boolean; kind: 'magazine' | 'energy' | 'none' }

/** The feed of the weapon in hand, as the combat HUD shows it. No row for this weapon yet = a full feed. */
export function ammoReadout(spec: WeaponSpec, row: FeedRow | null, nowMs: number): AmmoReadout {
  const feed = spec.feed;
  if (!feed) return { text: spec.kind === 'melee' ? 'melee' : '', frac: 1, fed: true, kind: 'none' };
  const fresh = !row || row.itemKey !== spec.key;
  if (feed.kind === 'magazine') {
    const max = feed.rounds;
    const reloadUntil = fresh ? 0 : row!.reloadUntilMs;
    const leftMs = reloadUntil > 0 ? reloadUntil - nowMs : 0;
    const reloading = leftMs > 0;
    const rounds = fresh ? max : (reloadUntil > 0 && leftMs <= 0 ? max : row!.rounds);
    return reloading
      ? { text: `RELOADING ${(leftMs / 1000).toFixed(1)} s`, frac: Math.max(0, Math.min(1, 1 - leftMs / feed.reloadMs)), fed: false, kind: 'magazine' }
      : { text: `${rounds} / ${max}`, frac: max > 0 ? rounds / max : 0, fed: rounds > 0, kind: 'magazine' };
  }
  const charge = fresh ? feed.chargeMax : chargeAfter(row!.charge, feed, nowMs - row!.chargeAtMs);
  const shots = Math.floor(charge / feed.shotCost);
  const fed = charge >= feed.shotCost;
  return {
    text: fed ? `${Math.round(charge)}% · ${shots} shot${shots === 1 ? '' : 's'}` : `RECHARGING ${Math.round(charge)}%`,
    frac: Math.max(0, Math.min(1, charge / feed.chargeMax)), fed, kind: 'energy',
  };
}

export interface InvItemIn { itemId: bigint; itemKey: string; name: string; quality: number; isWeapon: boolean }
export interface InvStackIn { stackId: bigint; name: string; familyKey: string; qty: number }
export interface InvRow {
  key: string; title: string; sub: string; icon: string;
  /** A weapon not in hand: the one-call equip. */
  equipItemId: bigint | null; inHand: boolean;
}

/** Weapons first (the one in hand at the top), then the other items, then the stacks by size — stable by name. */
export function inventoryRows(items: readonly InvItemIn[], stacks: readonly InvStackIn[], equippedKey: string, equippedQuality: number): InvRow[] {
  let inHandTaken = false;
  const rows = items.map((it) => {
    const inHand = it.isWeapon && !inHandTaken && it.itemKey === equippedKey && it.quality === equippedQuality;
    if (inHand) inHandTaken = true;
    return { it, inHand };
  });
  rows.sort((a, b) => (Number(b.inHand) - Number(a.inHand)) || (Number(b.it.isWeapon) - Number(a.it.isWeapon)) || a.it.name.localeCompare(b.it.name) || (a.it.itemId < b.it.itemId ? -1 : 1));
  const out: InvRow[] = rows.map(({ it, inHand }) => ({
    key: `item:${it.itemId}`,
    title: it.name || it.itemKey,
    sub: `${it.isWeapon ? 'weapon' : 'item'} · quality ${Math.round(it.quality)}${inHand ? ' · in hand' : ''}`,
    icon: it.isWeapon ? 'star' : 'box',
    equipItemId: it.isWeapon && !inHand ? it.itemId : null,
    inHand,
  }));
  const st = [...stacks].sort((a, b) => (b.qty - a.qty) || a.name.localeCompare(b.name));
  for (const s of st) out.push({ key: `stack:${s.stackId}`, title: s.name || s.familyKey, sub: `${s.familyKey.replace(/_/g, ' ')} × ${s.qty}`, icon: 'brick', equipItemId: null, inHand: false });
  return out;
}

/** "var(--ax-good)" → "--ax-good" (a chat line's colour, for the canvas's themeColor), else null for a literal. */
export function cssVarToken(color: string): string | null {
  const m = /^var\((--ax-[a-z0-9-]+)\)$/i.exec(color.trim());
  return m ? m[1] : null;
}
